import React, { useMemo, useState } from "react";

import { mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import languageCodeList from "../../../constants/languages";
import Dropdown from "../../dropdown/Dropdown";
import ConfirmModal from "../../modal/ConfirmModal";

import type { JSX } from "react";

import type {
  LanguageCode,
  ProjectTranslation,
  UpdatedProjectTranslation,
} from "../../../api/ProjectAPI.types";
import type { DropdownItem } from "../../dropdown/Dropdown";

interface ProjectLanguageSelectorProps {
  translations: (ProjectTranslation | UpdatedProjectTranslation)[];
  activeLanguageIndex: number;
  addDisabledTooltip: string | null;
  onSelectLanguage: (index: number) => void;
  onAddLanguage: (languageCode: LanguageCode) => void;
  onRemoveLanguage: (languageCode: LanguageCode) => void;
  onSetDefault: (languageCode: LanguageCode) => void;
}

export default function ProjectLanguageSelector({
  translations,
  activeLanguageIndex,
  addDisabledTooltip,
  onSelectLanguage,
  onAddLanguage,
  onRemoveLanguage,
  onSetDefault,
}: ProjectLanguageSelectorProps): JSX.Element | null {
  const { t } = useTranslation("languages");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const activeTranslation = translations[activeLanguageIndex];

  const languageKey = translations
    .map((translation) => translation.language)
    .join("|");

  const languageOptions = useMemo<DropdownItem<LanguageCode>[]>(() => {
    const existing = languageKey ? languageKey.split("|") : [];
    return languageCodeList
      .filter((code) => !existing.includes(code))
      .map((code) => {
        const label = t(`langs.${code}`);
        return {
          value: code,
          label: (
            <div className="text-muted-foreground hover:bg-muted flex w-full items-center justify-between gap-2.5 px-3.75 py-[0.46875rem]">
              <div>{label}</div>
              <div className="uppercase">{code}</div>
            </div>
          ),
          searchValue: `${label} ${code}`,
        };
      })
      .sort((a, b) => (a.searchValue ?? "").localeCompare(b.searchValue ?? ""));
  }, [languageKey, t]);

  if (!activeTranslation) return null;

  const currentLanguage = activeTranslation.language;

  return (
    <div className="border-border flex flex-wrap items-center justify-between gap-2.5 border-b pb-3.75">
      <div className="flex flex-col gap-1.25">
        <h2 className="text-foreground text-[1.40625rem] font-semibold">
          {t("languageManagement")}
        </h2>
        <div className="my-1.25 flex flex-wrap items-center gap-2.5">
          <div className="text-muted-foreground text-[0.9375rem]">
            {t("contentIn")}{" "}
            <span className="font-semibold uppercase">
              {t(`langs.${currentLanguage}`)}
            </span>
          </div>
          {activeTranslation.default ? (
            <span className="bg-muted text-muted-foreground rounded-[0.15625rem] px-1.25 py-[0.15625rem] text-[0.78125rem] font-medium">
              {t("defaultLanguage")}
            </span>
          ) : (
            <div className="flex flex-wrap gap-2.5">
              <button
                className="bg-primary/5 text-primary hover:bg-primary/10 flex cursor-pointer items-center rounded-[0.15625rem] px-1.25 py-[0.15625rem] text-[0.78125rem] font-medium"
                onClick={(): void => onSetDefault(currentLanguage)}
                type="button"
              >
                {t("selectAsDefault")}
              </button>
              <button
                className="bg-destructive/10 text-destructive hover:bg-destructive/20 flex cursor-pointer items-center rounded-[0.15625rem] px-1.25 py-[0.15625rem] text-[0.78125rem] font-medium"
                onClick={(): void => setIsDeleteModalOpen(true)}
                type="button"
              >
                {t("deleteLanguage")}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="border-border bg-muted flex flex-wrap items-stretch gap-2.5 rounded-[0.3125rem] border p-1.25">
        {translations.map((translation, index) => (
          <button
            key={translation.language}
            onClick={(): void => onSelectLanguage(index)}
            type="button"
            className={classnames(
              "relative flex min-w-11.25 cursor-pointer flex-col items-center justify-center rounded-lg border border-transparent px-2.5 py-[0.234375rem] text-sm transition-colors",
              index === activeLanguageIndex
                ? "text-primary border-border bg-card drop-shadow-sm"
                : "text-muted-foreground hover:bg-surface-muted hover:text-foreground",
            )}
          >
            <span className="font-semibold uppercase">
              {translation.language}
            </span>
            {translation.default ? (
              <span className="text-muted-foreground text-2xs -mt-1">
                {t("default")}
              </span>
            ) : null}
          </button>
        ))}
        {languageOptions.length > 0 ? (
          <div
            data-tooltip-content={addDisabledTooltip ?? undefined}
            data-tooltip-id={APP_TOOLTIP_ID}
          >
            <Dropdown
              withoutChevron
              withSearch
              align="right"
              disabled={Boolean(addDisabledTooltip)}
              icon={<Icon path={mdiPlus} size={0.9375} />}
              items={languageOptions}
              noResultsText={t("form:searchLanguageNoResults")}
              onClick={onAddLanguage}
              searchPlaceholder={t("form:searchLanguagePlaceholder")}
              title={t("selectLanguage")}
            />
          </div>
        ) : null}
      </div>

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={(): void => setIsDeleteModalOpen(false)}
        title={t("modal.delete.title")}
        actions={[
          {
            label: t("modal.delete.confirm"),
            buttonVariant: "delete",
            onClick: (): void => {
              onRemoveLanguage(currentLanguage);
              setIsDeleteModalOpen(false);
            },
          },
          {
            label: t("modal.delete.cancel"),
            onClick: (): void => setIsDeleteModalOpen(false),
          },
        ]}
      >
        <p>
          {t("modal.delete.body", {
            languageName: t(`langs.${currentLanguage}`),
          })}
        </p>
      </ConfirmModal>
    </div>
  );
}
